import { CloseIcon } from 'assets/icons/CloseIcon'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { ToggleButton } from 'components/Input/ToggleButton'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { createAttendanceDevice, editAttendanceDevice } from 'features/attendance/attendanceSlice'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { IAttendanceDevice } from 'types/api/attendanceApi.interface'
import { IAttendanceDeviceModalState } from 'types/attendanceDevices.interface'
import useFocusFirstInput from 'utils/useFocusFirstInput'

export const AttendanceDeviceModal = ({
  closeModal,
  value,
}: {
  closeModal: () => void
  value?: IAttendanceDevice
}) => {
  const [state, setState] = useState<IAttendanceDeviceModalState>({
    name: value?.name || '',
    serialNumber: value?.serialNumber || '',
    location: value?.locationId ? { label: String(value.locationId), value: value.locationId } : null,
    isActive: value?.isActive ?? true,
  })
  const dispatch = useDispatch<AppDispatch>()

  useFocusFirstInput()
  useModalClose(closeModal)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSelect = (value: ISelectValue, name: string) => {
    setState((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    const createReq = {
      name: state.name,
      vendor: 1,
      serialNumber: state.serialNumber || undefined,
      ipAddress: null,
      locationId: state.location?.value || undefined,
    }
    const editReq = {
      name: state.name,
      serialNumber: state.serialNumber || undefined,
      ipAddress: null,
      locationId: state.location?.value || undefined,
      isActive: state.isActive,
    }

    const resp = value?.id
      ? await dispatch(editAttendanceDevice({ id: value.id, req: editReq }))
      : await dispatch(createAttendanceDevice(createReq))

    if (resp.meta.requestStatus === 'fulfilled') {
      closeModal()
    }
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>{value?.id ? 'Edit' : 'Add'} Attendance Device</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <Flex gap='1rem'>
            <Input
              placeholder='Enter name...'
              labelText='Name'
              value={state.name}
              onChange={handleChange}
              name='name'
              nomarg
            />
            <Input
              placeholder='Enter serial number...'
              labelText='Serial Number'
              value={state.serialNumber}
              onChange={handleChange}
              name='serialNumber'
              nomarg
            />
          </Flex>
          <HonestSelectAsyncInput
            nomarg
            link='locations'
            placeholder='Search location'
            name='location'
            labelText='Location'
            defaultValue={state.location?.label}
            onChange={handleSelect}
            parserFunction={parseLocationRegistryOptions}
          />
          {value?.id ? (
            <ToggleButton
              start
              label='Active'
              state={state.isActive}
              handleChange={() => setState((prev) => ({ ...prev, isActive: !prev.isActive }))}
            />
          ) : null}
        </Flex>
        <Button mt='16' disabled={!state.name} orange onClick={handleSave}>
          {value?.id ? 'Save' : 'Create'}
        </Button>
      </Flex>
    </Modal>
  )
}
